/**
 * actionCooldown.js — RC 1.52 Phase 3A.4
 *
 * UX cooldown helper for paid / long-running CTAs. After a CTA
 * successfully dispatches, the Generate/Regenerate button must
 * remain disabled for a short window so the user doesn't:
 *
 *   • click again on a stale credits balance,
 *   • create unnecessary replay pressure,
 *   • produce duplicate variants on rapid retry.
 *
 * Rules (operator-locked 2026-02-12):
 *   • Cooldown starts AFTER a successful dispatch, NOT after
 *     validation/credit errors and NOT after backend failures.
 *   • Cooldown persists in `sessionStorage` so tab-switch and
 *     in-tab navigation cannot reset it. Hard refresh is permitted
 *     to clear it (acceptable per current product rule).
 *   • This is NOT a billing guard — the backend's idempotency,
 *     duplicate-title, and finalize-charge guards remain the
 *     authoritative safety nets. This is purely UX anti-double-
 *     click + anti-repeat-pressure.
 */

const STORE_KEY = 'ndor_action_cooldowns_v1';

// 5 minutes — operator-recommended for paid / long-running CTAs.
export const COOLDOWN_DEFAULT_MS = 5 * 60 * 1000;

// CV Variant cooldown is the FIRST surface to adopt this; other
// CTAs will follow in a later slice. Key format namespaces by
// action + user + job so a refresh on Job A doesn't lock Job B.
export function cvVariantCooldownKey(userId, jobId) {
  return `cv_variant::${userId || 'anon'}::${jobId || 'unknown'}`;
}

// RC 1.52 Phase 3B.1 — Analyse Fit cooldown is sourceId-scoped so
// a cooldown on Source CV doesn't lock re-running against a freshly
// generated variant, and vice-versa. `sourceId` may legitimately be
// absent for the wizard's pre-source-pick state — fall back to a
// stable sentinel so the key remains parseable.
export function analyseFitCooldownKey(userId, jobId, sourceId) {
  return `analyse_fit::${userId || 'anon'}::${jobId || 'unknown'}::${sourceId || 'no_source'}`;
}

// RC 1.52 Phase 3B.1 — Interview Prep cooldown is also sourceId-
// scoped so different CVs (variant vs Source) on the same job have
// independent cooldown windows. Mirrors the analyse_fit shape.
export function interviewPrepCooldownKey(userId, jobId, sourceId) {
  return `interview_prep::${userId || 'anon'}::${jobId || 'unknown'}::${sourceId || 'no_source'}`;
}

// ───────────────────────────────────────────────────────────────
// RC 1.52 Phase 3B.1 — README-style API aliases.
// The Phase 3B.1 brief asked for `isCooldownActive` /
// `getCooldownRemaining` names. The Phase 3A.4 helper shipped with
// `isOnCooldown` / `readCooldown`. Both names co-exist so existing
// CV callers stay byte-stable and the new Analyse Fit / Interview
// Prep callers can use the more discoverable names.
// ───────────────────────────────────────────────────────────────

export function isCooldownActive(key) {
  return isOnCooldown(key);
}

export function getCooldownRemaining(key) {
  const r = readCooldown(key);
  return r ? r.remainingMs : 0;
}

const _readAll = () => {
  try {
    if (typeof sessionStorage === 'undefined') return {};
    const raw = sessionStorage.getItem(STORE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch { return {}; }
};

const _writeAll = (map) => {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(STORE_KEY, JSON.stringify(map));
  } catch { /* quota / unavailable — fail-open */ }
};

/**
 * Start a cooldown for `key`, expiring `durationMs` from now.
 * Overwrites any existing entry for the same key.
 */
export function startCooldown(key, durationMs = COOLDOWN_DEFAULT_MS) {
  if (!key) return;
  const all = _readAll();
  all[key] = { until: Date.now() + Math.max(0, Number(durationMs) || 0) };
  _writeAll(all);
}

/** Forcibly clear a cooldown (e.g. on hard reset / dev tools). */
export function clearCooldown(key) {
  if (!key) return;
  const all = _readAll();
  if (key in all) {
    delete all[key];
    _writeAll(all);
  }
}

/**
 * Read the cooldown for `key`. Returns null if no cooldown is
 * active (or it has already expired — auto-purged opportunistically
 * so the store doesn't accumulate stale entries).
 */
export function readCooldown(key) {
  if (!key) return null;
  const all = _readAll();
  const rec = all[key];
  if (!rec || typeof rec.until !== 'number') return null;
  const remainingMs = rec.until - Date.now();
  if (remainingMs <= 0) {
    delete all[key];
    _writeAll(all);
    return null;
  }
  return { until: rec.until, remainingMs };
}

export function isOnCooldown(key) {
  return readCooldown(key) !== null;
}

/**
 * Format a "MM:SS" countdown label for the given remainingMs.
 * Negative / zero values render as "00:00".
 */
export function formatCooldownLabel(remainingMs) {
  const ms = Math.max(0, Number(remainingMs) || 0);
  const totalSec = Math.ceil(ms / 1000);
  const mm = String(Math.floor(totalSec / 60)).padStart(2, '0');
  const ss = String(totalSec % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

// ─── React hook ─────────────────────────────────────────────────
// Re-exported separately so callers that import the cooldown
// primitives in non-React contexts (e.g. unit-tests) don't drag
// the React runtime in.

import { useEffect, useState } from 'react';

/**
 * useCooldown(key) — re-renders every second while the cooldown
 * is active so the countdown label stays accurate. When the
 * cooldown ends, returns `{ active: false, remainingMs: 0, label: '' }`
 * and stops ticking until a new cooldown starts.
 */
export function useCooldown(key) {
  const initial = readCooldown(key);
  const [snapshot, setSnapshot] = useState(initial);

  useEffect(() => {
    if (!key) return undefined;
    // Refresh state every 1s while active. We do NOT keep ticking
    // forever — once the cooldown expires we clear the interval
    // and the consumer sees `active: false`.
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const next = readCooldown(key);
      setSnapshot(next);
    };
    tick(); // immediate sync on key change
    const id = setInterval(tick, 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [key]);

  if (!snapshot) {
    return { active: false, remainingMs: 0, label: '' };
  }
  return {
    active:      true,
    remainingMs: snapshot.remainingMs,
    label:       formatCooldownLabel(snapshot.remainingMs),
  };
}
